// src/lib/useMarketData.js
import { useState, useEffect, useRef, useCallback } from 'react'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001'

export function useMarketData(symbol, opts = {}) {
  const [quote,     setQuote]     = useState(null)
  const [connected, setConnected] = useState(false)

  const esRef          = useRef(null)
  const reconnectTimer = useRef(null)
  const mountedRef     = useRef(true)

  const openStream = useCallback(() => {
    if (!mountedRef.current) return
    esRef.current?.close()

    const token = localStorage.getItem('cf_access_token')
    if (!token) return

    const url = `${API_BASE}/marketdata/stream?symbols=${encodeURIComponent(symbol)}&token=${encodeURIComponent(token)}`
    const es  = new EventSource(url)
    esRef.current = es

    es.addEventListener('open', () => {
      if (!mountedRef.current) return
      setConnected(true)
    })

    es.addEventListener('quote', (e) => {
      if (!mountedRef.current) return
      try { setQuote(JSON.parse(e.data)) } catch {}
    })

    es.addEventListener('status', (e) => {
      if (!mountedRef.current) return
      try {
        const { connected: c } = JSON.parse(e.data)
        setConnected(c)
      } catch {}
    })

    es.addEventListener('error', () => {
      if (!mountedRef.current) return
      setConnected(false)
      es.close()
      reconnectTimer.current = setTimeout(openStream, 3000)
    })
  }, [symbol])

  useEffect(() => {
    mountedRef.current = true
    openStream()
    return () => {
      mountedRef.current = false
      clearTimeout(reconnectTimer.current)
      esRef.current?.close()
    }
  }, [symbol])

  // ── Derived values from Tradovate quote entries ───────────────
  const entries   = quote?.entries || {}
  const lastPrice = entries.Trade?.price        ?? null
  const bid       = entries.Bid?.price          ?? null
  const ask       = entries.Offer?.price        ?? null
  const openPrice = entries.OpeningPrice?.price ?? null
  const change    = (lastPrice != null && openPrice != null) ? lastPrice - openPrice : null
  const changePct = (change != null && openPrice)            ? (change / openPrice) * 100 : null
  const high      = entries.HighPrice?.price    ?? null
  const low       = entries.LowPrice?.price     ?? null
  const volume    = entries.TotalTradeVolume?.size ?? null

  return {
    quote,
    connected,
    lastPrice,
    bid,
    ask,
    change,
    changePct,
    high,
    low,
    volume,
  }
}
